import React from 'react';
import { Trash2, X } from 'lucide-react';
import { AppModal } from '../AppModal';
import { useDirtySnapshot } from '../../hooks/useDiscardGuard';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { AppSelect, type AppSelectOption } from '../AppSelect';
import { AppSelectDropdown } from '../AppSelectDropdown';
import { DatePicker } from '../DatePicker';
import { LoadingSpinner } from '../LoadingSpinner';
import {
  APP_BTN_DANGER_CLASS,
  APP_BTN_DANGER_OUTLINE_CLASS,
  APP_BTN_OUTLINE_CLASS,
  APP_BTN_PRIMARY_CLASS,
  APP_INPUT_CLASS,
  APP_TEXTAREA_CLASS,
} from '../../ui/controlStyles';
import { toHm } from '../../utils/employeeTimeEntryHours';
import { formatDateToDDMMYYYY, todayYMD } from '../../utils/date';

/** Existing interval being edited (appointment or project time). */
export type TimeEntryInterval = {
  id: string;
  employee_id: string;
  activity_id: string | null;
  lead_id?: string | null;
  entry_date: string;
  start_time: string | null;
  end_time: string | null;
  break_minutes?: number;
  note?: string;
};

type ActivityChoice = { id: string; title: string; due_at: string | null; employee_ids?: string[]; lead_title?: string };
type ProjectChoice = { id: string; title: string };

/**
 * - `lead`: project page. Employee is chosen, the project is fixed, appointments come from the page.
 * - `employee`: pay tab. Employee is fixed, appointments and projects are loaded for that employee.
 */
export type TimeEntryIntervalScope =
  | { kind: 'lead'; leadId: string; activities: ActivityChoice[] }
  | { kind: 'employee'; employeeId: string; employeeName: string };

type Target = 'activity' | 'lead';

/** Appointment picker value for time on the project without an appointment. */
const NO_ACTIVITY = '__none__';

type Overlap = { id: string; start_time: string | null; end_time: string | null };

export type TimeEntryIntervalModalProps = {
  open: boolean;
  onClose: () => void;
  /** Called after a save, delete, or submitted change request; `message` is shown by the caller if given. */
  onSaved: (message?: string) => void;
  scope: TimeEntryIntervalScope;
  /** Edit this interval; omit to create a new one. */
  entry?: TimeEntryInterval | null;
  /** Defaults for a new interval. */
  defaults?: { target?: Target; activityId?: string | null; date?: string };
  /**
   * Opened from an appointment card: the interval belongs to this appointment only
   * (no switch to project time, no appointment picker).
   */
  lockedActivityId?: string | null;
};

function localYmd(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function activityLabel(a: ActivityChoice): string {
  const ymd = localYmd(a.due_at);
  const datePart = ymd ? `${formatDateToDDMMYYYY(ymd)} · ` : '';
  const projectPart = a.lead_title ? ` (${a.lead_title})` : '';
  return `${datePart}${a.title || 'Termin'}${projectPart}`;
}

function intervalUrl(scope: TimeEntryIntervalScope, entryId?: string): string {
  const base =
    scope.kind === 'lead'
      ? `/api/leads/${encodeURIComponent(scope.leadId)}/time-entries`
      : `/api/employees/${encodeURIComponent(scope.employeeId)}/time-intervals`;
  return entryId ? `${base}/${encodeURIComponent(entryId)}` : base;
}

export function TimeEntryIntervalModal({
  open,
  onClose,
  onSaved,
  scope,
  entry,
  defaults,
  lockedActivityId = null,
}: TimeEntryIntervalModalProps) {
  const isEdit = Boolean(entry?.id);
  // Projektseite: /leads/:id/time-entries braucht die Module Projekte und Mitarbeiter.
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const intervalApiAvailable =
    scope.kind !== 'lead' || (modulesLoaded && isEnabled('projekte') && isEnabled('hr'));
  const [employeeId, setEmployeeId] = React.useState('');
  const [target, setTarget] = React.useState<Target>('activity');
  const [activityId, setActivityId] = React.useState('');
  const [leadId, setLeadId] = React.useState('');
  const [date, setDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [breakMinutes, setBreakMinutes] = React.useState('0');
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [overlaps, setOverlaps] = React.useState<Overlap[] | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [formKey, setFormKey] = React.useState(0);

  const [employees, setEmployees] = React.useState<Array<{ id: string; name: string }>>([]);
  const [employeeActivities, setEmployeeActivities] = React.useState<ActivityChoice[]>([]);
  const [projects, setProjects] = React.useState<ProjectChoice[]>([]);
  const [projectQuery, setProjectQuery] = React.useState('');

  // Reset the form whenever the dialog opens (callers may pass fresh `scope`/`defaults` objects
  // on every render, so only `open` and the edited entry trigger a reset).
  const latestProps = React.useRef({ entry, defaults, scope, lockedActivityId });
  latestProps.current = { entry, defaults, scope, lockedActivityId };
  React.useEffect(() => {
    if (!open) return;
    const { entry, defaults, scope, lockedActivityId } = latestProps.current;
    const initialTarget: Target = lockedActivityId
      ? 'activity'
      : entry
        ? (entry.activity_id ? 'activity' : 'lead')
        : defaults?.target ?? 'activity';
    const initialActivity = entry?.activity_id ?? lockedActivityId ?? defaults?.activityId ?? '';
    const activityDate =
      scope.kind === 'lead' && initialActivity
        ? localYmd(scope.activities.find((a) => a.id === initialActivity)?.due_at)
        : '';
    setEmployeeId(entry?.employee_id ?? (scope.kind === 'employee' ? scope.employeeId : ''));
    setTarget(initialTarget);
    setActivityId(initialTarget === 'lead' ? NO_ACTIVITY : initialActivity);
    setLeadId(entry?.lead_id ?? (scope.kind === 'lead' ? scope.leadId : ''));
    setDate(entry?.entry_date ?? defaults?.date ?? (activityDate || todayYMD()));
    setStartTime(toHm(entry?.start_time ?? null));
    setEndTime(toHm(entry?.end_time ?? null));
    setBreakMinutes(String(entry?.break_minutes ?? 0));
    setNote(entry?.note ?? '');
    setError(null);
    setOverlaps(null);
    setProjectQuery('');
    setConfirmDelete(false);
    setFormKey((k) => k + 1);
  }, [open, entry?.id]);
  // Vergleichsbasis nach dem Zurücksetzen oben — vorbelegte Werte zählen nicht als Eingabe.
  const dirty = useDirtySnapshot(
    JSON.stringify([employeeId, target, activityId, leadId, date, startTime, endTime, breakMinutes, note.trim()]),
    formKey,
  );

  // A changed interval needs a fresh overlap check.
  React.useEffect(() => {
    setOverlaps(null);
  }, [employeeId, date, startTime, endTime]);

  // Project page: employee list (assignees of the chosen appointment are sorted first below).
  React.useEffect(() => {
    if (!open || scope.kind !== 'lead') return;
    let cancelled = false;
    fetch('/api/employees/picker', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((data: { items?: Array<{ id: string; first_name: string; last_name: string }> }) => {
        if (cancelled) return;
        setEmployees(
          (Array.isArray(data.items) ? data.items : []).map((e) => ({
            id: e.id,
            name: `${e.first_name} ${e.last_name}`.trim() || 'Mitarbeiter',
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setEmployees([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, scope.kind]);

  // Pay tab: the employee's appointments around the date, and projects (search or from those appointments).
  const employeeScopeId = scope.kind === 'employee' ? scope.employeeId : null;
  React.useEffect(() => {
    if (!open || !employeeScopeId || !date) return;
    let cancelled = false;
    const handle = window.setTimeout(() => {
      const params = new URLSearchParams({ date, q: projectQuery });
      fetch(`/api/employees/${encodeURIComponent(employeeScopeId)}/time-interval-options?${params}`, {
        credentials: 'include',
      })
        .then((res) => (res.ok ? res.json() : { activities: [], projects: [] }))
        .then((data: { activities?: ActivityChoice[]; projects?: ProjectChoice[] }) => {
          if (cancelled) return;
          setEmployeeActivities(Array.isArray(data.activities) ? data.activities : []);
          setProjects(Array.isArray(data.projects) ? data.projects : []);
        })
        .catch(() => undefined);
    }, projectQuery ? 250 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [open, employeeScopeId, date, projectQuery]);

  const activities: ActivityChoice[] = scope.kind === 'lead' ? scope.activities : employeeActivities;
  const selectedActivity = activities.find((a) => a.id === activityId) ?? null;

  const employeeOptions = React.useMemo(() => {
    const assigned = new Set(selectedActivity?.employee_ids ?? []);
    const sorted = [...employees].sort((a, b) => {
      const rank = Number(assigned.has(b.id)) - Number(assigned.has(a.id));
      return rank !== 0 ? rank : a.name.localeCompare(b.name, 'de');
    });
    return sorted.map((e) => ({
      value: e.id,
      label: e.name,
      description: assigned.has(e.id) ? 'Im Termin eingeplant' : undefined,
    }));
  }, [employees, selectedActivity]);

  const activityOptions: AppSelectOption[] = [
    { value: '', label: 'Termin auswählen' },
    { value: NO_ACTIVITY, label: 'Kein Termin' },
    ...activities.map((a) => ({ value: a.id, label: activityLabel(a) })),
  ];
  // Keep the current project selectable even when a search does not return it.
  const projectChoices =
    leadId && !projects.some((p) => p.id === leadId) && entry?.lead_id === leadId
      ? [{ id: leadId, title: 'Aktuelles Projekt' }, ...projects]
      : projects;
  const projectOptions: AppSelectOption[] = [
    { value: '', label: projectChoices.length > 0 ? 'Projekt auswählen' : 'Kein Projekt gefunden' },
    ...projectChoices.map((p) => ({ value: p.id, label: p.title || 'Projekt' })),
  ];

  const onActivityChange = (next: string) => {
    setActivityId(next);
    setTarget(next === NO_ACTIVITY ? 'lead' : 'activity');
    const ymd = localYmd(activities.find((a) => a.id === next)?.due_at);
    if (ymd && scope.kind === 'lead' && !isEdit) setDate(ymd);
  };

  const submit = async (confirmOverlap: boolean) => {
    if (!intervalApiAvailable) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(intervalUrl(scope, entry?.id), {
        method: isEdit ? 'PATCH' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: employeeId,
          target,
          activity_id: target === 'activity' ? activityId : null,
          lead_id: target === 'lead' ? leadId : null,
          entry_date: date,
          start_time: startTime,
          end_time: endTime,
          break_minutes: Number.parseInt(breakMinutes, 10) || 0,
          note,
          confirm_overlap: confirmOverlap,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        code?: string;
        overlaps?: Overlap[];
        change_request?: unknown;
      };
      if (res.status === 409 && data.code === 'overlap') {
        setOverlaps(Array.isArray(data.overlaps) ? data.overlaps : []);
        return;
      }
      if (res.status === 202 && data.change_request) {
        onSaved('Änderungsantrag eingereicht. Die Ist-Zeit wird nach Freigabe wirksam.');
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Ist-Zeit konnte nicht gespeichert werden.');
      onSaved();
    } catch (e) {
      setOverlaps(null);
      setError(e instanceof Error ? e.message : 'Ist-Zeit konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!entry?.id || !intervalApiAvailable) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(intervalUrl(scope, entry.id), { method: 'DELETE', credentials: 'include' });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Ist-Zeit konnte nicht gelöscht werden.');
      onSaved();
    } catch (e) {
      setConfirmDelete(false);
      setError(e instanceof Error ? e.message : 'Ist-Zeit konnte nicht gelöscht werden.');
    } finally {
      setDeleting(false);
    }
  };

  const canSubmit =
    intervalApiAvailable &&
    Boolean(employeeId) &&
    Boolean(date) &&
    Boolean(startTime) &&
    Boolean(endTime) &&
    (target === 'activity' ? Boolean(activityId) : Boolean(leadId));

  const labelClass = 'text-xs font-medium text-app-text-muted';

  return (
    <>
      <AppModal
        open={open}
        onClose={onClose}
        overlayClassName="z-[60]"
        panelClassName="md:max-w-lg md:w-full"
        ariaLabelledBy="time-interval-title"
        dirty={open && dirty}
        disableClose={saving || deleting}
      >
        {(requestClose) => (
          <div className="flex min-h-0 flex-1 flex-col md:max-h-[90vh]">
            <div className="flex shrink-0 items-center justify-between border-b border-app-border px-5 py-4">
              <div>
                <h2 id="time-interval-title" className="text-base font-semibold text-app-text">
                  {isEdit ? 'Ist-Zeit bearbeiten' : 'Ist-Zeit erfassen'}
                </h2>
                {scope.kind === 'employee' ? (
                  <p className="mt-0.5 text-xs text-app-text-subtle">{scope.employeeName}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={requestClose}
                className="rounded-md p-1.5 text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
              {lockedActivityId ? (
                <div>
                  <span className={labelClass}>Termin</span>
                  <p className="mt-1 text-sm text-app-text">
                    {selectedActivity ? activityLabel(selectedActivity) : 'Termin'}
                  </p>
                </div>
              ) : (
                <label className="block">
                  <span className={labelClass}>Termin</span>
                  <AppSelect value={activityId} onChange={onActivityChange} options={activityOptions} className="mt-1" />
                </label>
              )}

              {target !== 'lead' ? null : scope.kind === 'employee' ? (
                <div>
                  <span className={labelClass}>Projekt</span>
                  <input
                    value={projectQuery}
                    onChange={(e) => setProjectQuery(e.target.value)}
                    placeholder="Projekt suchen"
                    className={`${APP_INPUT_CLASS} mt-1`}
                  />
                  <AppSelect value={leadId} onChange={setLeadId} options={projectOptions} className="mt-2" />
                </div>
              ) : (
                <p className="text-xs text-app-text-subtle">
                  Die Zeit wird diesem Projekt zugeordnet, ohne Termin (z. B. Nacharbeit).
                </p>
              )}

              {scope.kind === 'lead' ? (
                <label className="block">
                  <span className={labelClass}>Mitarbeiter</span>
                  <AppSelectDropdown
                    value={employeeId}
                    onChange={setEmployeeId}
                    options={employeeOptions}
                    placeholder="Mitarbeiter auswählen"
                    searchable
                    searchPlaceholder="Mitarbeiter suchen…"
                    disabled={isEdit}
                    className="mt-1"
                  />
                  {isEdit ? (
                    <span className="mt-1 block text-xs text-app-text-subtle">
                      Für einen anderen Mitarbeiter bitte löschen und neu anlegen.
                    </span>
                  ) : null}
                </label>
              ) : null}

              <label className="block">
                <span className={labelClass}>Datum</span>
                <DatePicker
                  value={date}
                  onChange={(next) => next && setDate(next)}
                  className="mt-1 w-full"
                  // The pay-tab popover closes on clicks outside its DOM, so no portal there.
                  menuZIndex={scope.kind === 'lead' ? 80 : undefined}
                />
              </label>

              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className={labelClass}>Von</span>
                  <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={`${APP_INPUT_CLASS} mt-1`} />
                </label>
                <label className="block">
                  <span className={labelClass}>Bis</span>
                  <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={`${APP_INPUT_CLASS} mt-1`} />
                </label>
                <label className="block">
                  <span className={labelClass}>Pause (Min.)</span>
                  <input
                    type="number"
                    min={0}
                    step={5}
                    value={breakMinutes}
                    onChange={(e) => setBreakMinutes(e.target.value)}
                    className={`${APP_INPUT_CLASS} mt-1`}
                  />
                </label>
              </div>

              <label className="block">
                <span className={labelClass}>Notiz</span>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={`${APP_TEXTAREA_CLASS} mt-1`} />
              </label>

              {overlaps ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <p className="font-medium">Überschneidung mit einer anderen Arbeitszeit an diesem Tag:</p>
                  <ul className="mt-1 list-disc pl-4">
                    {overlaps.map((o) => (
                      <li key={o.id}>
                        {toHm(o.start_time) || '–'}–{toHm(o.end_time) || 'offen'}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {error ? <p className="text-sm text-red-700">{error}</p> : null}
            </div>

            <div className="flex shrink-0 items-center justify-between gap-2 border-t border-app-border px-5 py-3">
              {/* Inline confirm: no portal, so the dialog also works inside the pay-tab popover. */}
              <div className="flex items-center gap-2">
                {isEdit && !confirmDelete && intervalApiAvailable ? (
                  <button type="button" onClick={() => setConfirmDelete(true)} className={APP_BTN_DANGER_OUTLINE_CLASS} disabled={saving}>
                    <Trash2 className="h-4 w-4" />
                    Löschen
                  </button>
                ) : null}
                {isEdit && confirmDelete ? (
                  <>
                    <button type="button" onClick={() => void remove()} className={APP_BTN_DANGER_CLASS} disabled={deleting}>
                      {deleting ? <LoadingSpinner size="sm" /> : <Trash2 className="h-4 w-4" />}
                      Wirklich löschen
                    </button>
                    <button type="button" onClick={() => setConfirmDelete(false)} className="text-sm text-app-text-muted hover:text-app-text">
                      Nein
                    </button>
                  </>
                ) : null}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className={APP_BTN_OUTLINE_CLASS}>
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={() => void submit(Boolean(overlaps))}
                  disabled={!canSubmit || saving}
                  className={APP_BTN_PRIMARY_CLASS}
                >
                  {saving ? <LoadingSpinner size="sm" /> : null}
                  {overlaps ? 'Trotzdem speichern' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AppModal>
    </>
  );
}
